import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import {
  Calendar as CalendarIcon,
  FileSearch,
  ChevronDown,
  Search,
} from 'lucide-react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import TablePagination from 'src/components/common/TablePagination';
import DataTable from 'src/components/ui/data-table';
import { Calendar } from 'src/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { cn } from 'src/lib/utils';
import { useResumeTrackerDashboard } from 'src/services/api/queries/resumeTrackerQueries';
import { STATUS_COLORS } from './statusMeta';

const API_DATE_FORMAT = 'yyyy-MM-dd';
const DISPLAY_DATE_FORMAT = 'dd MMM yyyy';

const SOURCE_DISPLAY = {
  linkedin: { logo: 'in', color: '#0A66C2', name: 'LinkedIn' },
  naukri: { logo: 'N', color: '#0E7C7B', name: 'Naukri' },
  ahedge: { logo: 'A', color: '#5cd6eb', name: 'Ahedge' },
  internal: { logo: 'In', color: '#5cd6eb', name: 'Internal' },
};

const getSourceMeta = (source) => {
  const key = String(source).toLowerCase();
  return (
    SOURCE_DISPLAY[key] ?? {
      logo: key.slice(0, 2).toUpperCase(),
      color: '#64748b',
      name: source.charAt(0).toUpperCase() + source.slice(1),
    }
  );
};

const PlatCell = ({ source }) => {
  if (!source) {
    return (
      <span className="inline-flex items-center gap-[7px]">
        <span className="w-[22px] h-[22px] min-w-[22px] rounded-[6px] flex items-center justify-center text-[10.5px] font-bold text-muted-foreground bg-muted">
          ?
        </span>
        <span className="text-[12.5px] font-[450] text-muted-foreground">
          Unassigned
        </span>
      </span>
    );
  }
  const meta = getSourceMeta(source);
  return (
    <span className="inline-flex items-center gap-[7px]">
      <span
        className="w-[22px] h-[22px] min-w-[22px] rounded-[6px] flex items-center justify-center text-[10.5px] font-bold text-white"
        style={{ background: meta.color }}
      >
        {meta.logo}
      </span>
      <span className="text-[12.5px] font-[500] text-foreground">
        {meta.name}
      </span>
    </span>
  );
};

const PRESETS = [
  { key: 'last30', label: 'Last 30 days', days: 30 },
  { key: 'last60', label: 'Last 60 days', days: 60 },
];

const TAB_BASE =
  'inline-flex items-center gap-[7px] px-[18px] py-[10px] text-[13px] font-medium border-b-2 -mb-px transition-colors cursor-pointer bg-transparent border-x-0 border-t-0 whitespace-nowrap font-[inherit]';
const TAB_ACTIVE = 'text-primary font-semibold border-primary';
const TAB_INACTIVE =
  'text-muted-foreground border-transparent hover:text-foreground';

const CountCell = ({ value, status }) => {
  const count = value ?? 0;
  if (count === 0)
    return <span className="text-sm text-muted-foreground font-[450]">0</span>;
  return (
    <span
      className="text-sm font-semibold tabular-nums"
      style={{ color: STATUS_COLORS[status] }}
    >
      {count}
    </span>
  );
};

const SourcePill = ({ logo, logoColor, name, count, loading }) => (
  <div className="flex items-center gap-[11px] p-[10px_16px_10px_12px] rounded-[10px] border border-borderColorSecondary bg-muted min-w-[170px]">
    <div
      className="w-[34px] h-[34px] min-w-[34px] rounded-[8px] flex items-center justify-center text-[14px] font-bold text-white shrink-0"
      style={{ background: logoColor }}
    >
      {logo}
    </div>
    <div className="flex flex-col leading-[1.25]">
      <span className="text-[13px] font-semibold text-foreground-strong">
        {name}
      </span>
      <span className="text-[11.5px] text-muted-foreground">
        {loading ? (
          <span className="inline-block w-10 h-3 rounded bg-muted/60 animate-pulse" />
        ) : (
          <>
            <span className="font-bold tabular-nums text-ds-success">
              {count ?? 0}
            </span>{' '}
            completed
          </>
        )}
      </span>
    </div>
  </div>
);

/* Metric chip used in Zone 3 */
const MetricChip = ({ value, label, valueClassName, loading }) => (
  <div className="flex flex-col items-center justify-center min-w-[84px] px-[12px] py-[8px] rounded-[10px] border border-borderColorSecondary bg-muted">
    {loading ? (
      <span className="inline-block w-8 h-5 rounded bg-muted animate-pulse mb-1" />
    ) : (
      <span
        className={cn(
          'text-[20px] font-bold tracking-[-0.02em] leading-[1.1] tabular-nums text-foreground-strong',
          valueClassName
        )}
      >
        {value ?? 0}
      </span>
    )}
    <span className="text-[10.5px] font-medium uppercase tracking-[0.04em] text-muted-foreground mt-[3px]">
      {label}
    </span>
  </div>
);

const DEFAULT_PAGE_SIZE = 10;

const UploadTracker = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState('last30');
  const [range, setRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );

  const fromStr = range?.from ? format(range.from, API_DATE_FORMAT) : null;
  const toStr = range?.to ? format(range.to, API_DATE_FORMAT) : null;

  const { data: result, isLoading } = useResumeTrackerDashboard(
    fromStr,
    toStr,
    currentPage,
    currentPageSize
  );

  const summary = result?.data?.summary ?? {};
  const jobs = useMemo(() => result?.data?.jobs ?? [], [result]);
  const serverPagination = result?.data?.pagination ?? null;

  const updateParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    setSearchParams(next);
  };

  const handlePageChange = (page) => updateParams({ page });
  const handlePageSizeChange = (size) =>
    updateParams({ pageSize: size, page: 1 });

  const filteredJobs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter((job) =>
      [job.job_name, job.job_ref, job.source]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [jobs, searchQuery]);

  const totals = useMemo(
    () =>
      jobs.reduce(
        (acc, job) => {
          const sc = job.status_counts ?? {};
          acc.started += sc.STARTED ?? 0;
          acc.completed += sc.COMPLETED ?? 0;
          acc.failed += sc.FAILED ?? 0;
          acc.duplicate += sc.DUPLICATE ?? 0;
          return acc;
        },
        { started: 0, completed: 0, failed: 0, duplicate: 0 }
      ),
    [jobs]
  );

  const handlePreset = (preset) => {
    setMode(preset.key);
    setRange({ from: subDays(new Date(), preset.days), to: new Date() });
  };

  const handleRangeSelect = (selected) => {
    setRange(selected);
    setMode('custom');
    setIsPickerOpen(false);
  };

  const customLabel =
    mode === 'custom' && range?.from && range?.to
      ? `${format(range.from, DISPLAY_DATE_FORMAT)} – ${format(range.to, DISPLAY_DATE_FORMAT)}`
      : 'Custom';

  const activeDateLabel =
    mode !== 'custom'
      ? (PRESETS.find((p) => p.key === mode)?.label ?? '')
      : customLabel;

  const completedSourceEntries = useMemo(
    () => Object.entries(summary.completed_by_source ?? {}),
    [summary]
  );

  const totalCompleted = completedSourceEntries.reduce(
    (sum, [, count]) => sum + (count ?? 0),
    0
  );

  const columns = [
    {
      key: 'job_name',
      header: 'Job',
      width: '45%',
      className: 'text-left',
      render: (value, row) => (
        <div className="flex flex-col gap-[2px]">
          <span className="text-[14px] font-semibold text-foreground-strong tracking-[-0.01em] break-words">
            {value || '-'}
          </span>
          {row.job_ref && (
            <span className="font-mono text-[11px] font-medium text-muted-foreground break-all">
              {row.job_ref}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      width: '15%',
      className: 'text-left',
      render: (value) => <PlatCell source={value} />,
    },
    {
      key: 'started',
      header: 'Started',
      width: '10%',
      align: 'center',
      className: 'text-center',
      render: (_v, row) => (
        <CountCell value={row.status_counts?.STARTED} status="STARTED" />
      ),
    },
    {
      key: 'completed',
      header: 'Completed',
      width: '10%',
      align: 'center',
      className: 'text-center',
      render: (_v, row) => (
        <CountCell value={row.status_counts?.COMPLETED} status="COMPLETED" />
      ),
    },
    {
      key: 'failed',
      header: 'Failed',
      width: '10%',
      align: 'center',
      className: 'text-center',
      render: (_v, row) => (
        <CountCell value={row.status_counts?.FAILED} status="FAILED" />
      ),
    },
    {
      key: 'duplicate',
      header: 'Duplicate',
      width: '10%',
      align: 'center',
      className: 'text-center',
      render: (_v, row) => (
        <CountCell value={row.status_counts?.DUPLICATE} status="DUPLICATE" />
      ),
    },
  ];

  const emptyStateNode = (
    <div className="flex flex-col items-center gap-[10px] py-12 px-4 text-center">
      <FileSearch className="w-[26px] h-[26px] text-border" strokeWidth={1.6} />
      <span className="text-[13.5px] font-semibold text-muted-foreground">
        No uploads found
      </span>
      <span className="text-[12.5px] text-muted-foreground">
        No resume uploads were recorded for the selected date range. Try
        adjusting the range.
      </span>
    </div>
  );

  return (
    <div>
      <PageHeader title="Upload Tracker" />

      <PageContainer className="space-y-5">
        {/* Page header */}
        <header className="mb-[22px]">
          <h1 className="text-[22px] font-bold tracking-[-0.04em] text-foreground-strong m-0 mb-1">
            Upload Tracker
          </h1>
          <p className="text-[13.5px] text-muted-foreground m-0 leading-[1.55]">
            Every job being extracted, with live counts of candidate profiles
            started, completed, failed, and flagged as duplicates.
          </p>
        </header>

        {/* Overview section */}
        <section aria-label="Overview">
          <button
            type="button"
            onClick={() => setOverviewOpen((o) => !o)}
            aria-expanded={overviewOpen}
            className="group w-full flex items-center gap-2.5 mb-3.5 bg-transparent border-none p-0 cursor-pointer"
          >
            <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Overview
            </span>
            <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
              <CalendarIcon className="w-[13px] h-[13px]" strokeWidth={1.8} />
              {activeDateLabel}
            </span>
            <span
              className={cn(
                'w-6 h-6 rounded-[6px] flex items-center justify-center text-muted-foreground transition-all duration-200 group-hover:bg-borderColorSecondary group-hover:text-foreground',
                overviewOpen ? '' : '-rotate-90'
              )}
            >
              <ChevronDown className="w-4 h-4" strokeWidth={2} />
            </span>
          </button>

          {/* Collapsible body */}
          <div
            className={cn(
              'grid transition-[grid-template-rows,opacity] duration-300 ease-in-out',
              overviewOpen
                ? 'grid-rows-[1fr] opacity-100'
                : 'grid-rows-[0fr] opacity-0'
            )}
          >
            <div className="overflow-hidden">
              {/* Date range tabs */}
              <div
                className="flex items-center border-b border-borderColorSecondary mb-[18px]"
                role="tablist"
                aria-label="Date range"
              >
                {PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    role="tab"
                    aria-selected={mode === preset.key}
                    onClick={() => handlePreset(preset)}
                    className={cn(
                      TAB_BASE,
                      mode === preset.key ? TAB_ACTIVE : TAB_INACTIVE
                    )}
                  >
                    <CalendarIcon className="w-3.5 h-3.5" strokeWidth={1.8} />
                    {preset.label}
                  </button>
                ))}

                <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={mode === 'custom'}
                      className={cn(
                        TAB_BASE,
                        mode === 'custom' ? TAB_ACTIVE : TAB_INACTIVE
                      )}
                    >
                      <CalendarIcon className="w-3.5 h-3.5" strokeWidth={1.8} />
                      Custom
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-0">
                    <Calendar
                      mode="range"
                      selected={range}
                      onSelect={handleRangeSelect}
                      numberOfMonths={2}
                      defaultMonth={range?.from}
                      disabled={{ after: new Date() }}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Summary card — 3 zones */}
              <div className="bg-card rounded-xl border border-borderColorSecondary shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-[16px_20px] flex items-stretch gap-6 mb-5">
                {/* Zone 1: Completed by source (grows) */}
                <div className="flex flex-col min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                      Completed by source
                    </span>
                    <span className="text-[12px] text-muted-foreground">
                      <strong className="text-[15px] text-foreground-strong font-bold tabular-nums">
                        {isLoading ? '—' : totalCompleted}
                      </strong>{' '}
                      completed
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {isLoading &&
                      [0, 1].map((i) => (
                        <div
                          key={i}
                          className="flex items-center gap-[11px] p-[10px_16px_10px_12px] rounded-[10px] border border-borderColorSecondary bg-muted min-w-[170px]"
                        >
                          <div className="w-[34px] h-[34px] min-w-[34px] rounded-[8px] bg-muted/60 animate-pulse" />
                          <div className="flex flex-col gap-1.5">
                            <span className="inline-block w-16 h-3 rounded bg-muted/60 animate-pulse" />
                            <span className="inline-block w-20 h-2.5 rounded bg-muted/60 animate-pulse" />
                          </div>
                        </div>
                      ))}
                    {!isLoading &&
                      completedSourceEntries.map(([source, count]) => {
                        const meta = getSourceMeta(source);
                        return (
                          <SourcePill
                            key={source}
                            logo={meta.logo}
                            logoColor={meta.color}
                            name={meta.name}
                            count={count}
                          />
                        );
                      })}
                    {!isLoading && completedSourceEntries.length === 0 && (
                      <span className="text-[12.5px] text-muted-foreground py-2">
                        No completed uploads in this range.
                      </span>
                    )}
                  </div>
                </div>

                {/* Divider */}
                <div
                  className="w-px bg-borderColorSecondary self-stretch flex-shrink-0"
                  role="separator"
                  aria-hidden="true"
                />

                {/* Zone 2: Total jobs */}
                <div className="flex flex-col flex-shrink-0 min-w-[90px]">
                  <div className="mb-3">
                    <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                      Total Jobs
                    </span>
                  </div>
                  <div className="flex items-center justify-center flex-1">
                    {isLoading ? (
                      <span className="inline-block w-10 h-8 rounded bg-muted animate-pulse" />
                    ) : (
                      <span className="text-[30px] font-bold tracking-[-0.03em] leading-none text-foreground-strong tabular-nums">
                        {jobs.length}
                      </span>
                    )}
                  </div>
                </div>

                {/* Divider */}
                <div
                  className="w-px bg-borderColorSecondary self-stretch flex-shrink-0"
                  role="separator"
                  aria-hidden="true"
                />

                {/* Zone 3: Profile upload status */}
                <div className="flex flex-col flex-shrink-0">
                  <div className="mb-3">
                    <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                      Profile upload status
                    </span>
                  </div>
                  <div className="flex gap-[10px] flex-wrap">
                    <MetricChip
                      value={totals.started}
                      label="Started"
                      loading={isLoading}
                    />
                    <MetricChip
                      value={totals.failed}
                      label="Failed"
                      valueClassName="text-ds-danger"
                      loading={isLoading}
                    />
                    <MetricChip
                      value={totals.duplicate}
                      label="Duplicate"
                      valueClassName="text-ds-warning"
                      loading={isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Toolbar — search */}
        <div className="flex items-center justify-end">
          <div className="relative">
            <Search
              className="absolute left-[11px] top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none"
              strokeWidth={1.8}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search job or source…"
              className="pl-[34px] pr-3 py-2 rounded-lg border border-input bg-card text-[12.5px] text-foreground-strong w-[250px] outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] transition-[border-color,box-shadow] placeholder:text-muted-foreground"
            />
          </div>
        </div>

        {/* Jobs table */}
        <DataTable
          className="border-border shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
          data={filteredJobs}
          columns={columns}
          loading={isLoading}
          emptyState={emptyStateNode}
          headerWhenEmpty
          cellClassName={() => 'py-[13px] px-[16px]'}
          onRowClick={(row) =>
            navigate(`/upload-tracker/${encodeURIComponent(row.job_ref)}`, {
              state: {
                job: row,
                dateFrom: fromStr,
                dateTo: toStr,
                dateMode: mode,
              },
            })
          }
        />
        {jobs.length > 0 && (
          <TablePagination
            currentPage={currentPage}
            totalPages={
              serverPagination?.total_pages ??
              serverPagination?.pages ??
              Math.max(1, Math.ceil(jobs.length / currentPageSize))
            }
            pageSize={currentPageSize}
            totalItems={serverPagination?.total ?? jobs.length}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="jobs"
          />
        )}
      </PageContainer>
    </div>
  );
};

export default UploadTracker;
